import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { standardQueryKeys } from '@/lib/standardizedQueryDefaults';
import { procurementQueryKeys } from '@/lib/procurement/queryKeys';
import { logger } from '@/lib/logger';

/**
 * Smart Prefetching Hook
 * Implements hover-based and route-based prefetching strategies
 * 
 * Performance target: < 100ms perceived navigation time
 * Strategy: Prefetch on hover (300ms delay) + route prediction
 */

interface PrefetchOptions {
  delay?: number;
  staleTime?: number;
  priority?: 'low' | 'normal' | 'high';
}

export const useSmartPrefetch = () => {
  const queryClient = useQueryClient();
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const prefetchedRef = useRef<Set<string>>(new Set());

  /**
   * Prefetch projects list (main landing page data)
   */
  const prefetchProjects = useCallback((options: PrefetchOptions = {}) => {
    const { delay = 300, staleTime = 60000 } = options;
    const key = 'projects-list';

    if (prefetchedRef.current.has(key)) {
      return; // Already prefetched
    }

    const existingTimer = timersRef.current.get(key);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: standardQueryKeys.projects.lists(),
        staleTime,
      });
      prefetchedRef.current.add(key);
      timersRef.current.delete(key);
      logger.debug('PREFETCH', 'Projects list prefetched');
    }, delay);

    timersRef.current.set(key, timer);
  }, [queryClient]);

  /**
   * Prefetch specific project details
   */
  const prefetchProject = useCallback((projectId: string, options: PrefetchOptions = {}) => {
    const { delay = 300, staleTime = 60000 } = options;
    const key = `project-${projectId}`;

    if (prefetchedRef.current.has(key)) {
      return;
    }

    const existingTimer = timersRef.current.get(key);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(() => {
      // Prefetch project detail
      queryClient.prefetchQuery({
        queryKey: standardQueryKeys.projects.detail(projectId),
        staleTime,
      });

      // Prefetch related data
      queryClient.prefetchQuery({
        queryKey: standardQueryKeys.diary.project(projectId),
        staleTime: 30000, // 30s for diary data
      });

      queryClient.prefetchQuery({
        queryKey: procurementQueryKeys.data(projectId),
        staleTime: 45000, // 45s for procurement
      });

      prefetchedRef.current.add(key);
      timersRef.current.delete(key);
      logger.debug('PREFETCH', `Project ${projectId} prefetched with related data`);
    }, delay);

    timersRef.current.set(key, timer);
  }, [queryClient]);

  /**
   * Prefetch diary/specs data for a project
   */
  const prefetchDiary = useCallback((projectId: string, options: PrefetchOptions = {}) => {
    const { delay = 300, staleTime = 30000 } = options;
    const key = `diary-${projectId}`;

    if (prefetchedRef.current.has(key)) {
      return;
    }

    const existingTimer = timersRef.current.get(key);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: standardQueryKeys.diary.photos(projectId),
        staleTime,
      });
      queryClient.prefetchQuery({
        queryKey: standardQueryKeys.specs.data(projectId),
        staleTime,
      });

      prefetchedRef.current.add(key);
      timersRef.current.delete(key);
      logger.debug('PREFETCH', `Diary/Specs for ${projectId} prefetched`);
    }, delay);

    timersRef.current.set(key, timer);
  }, [queryClient]);

  /**
   * Prefetch admin dashboard data
   */
  const prefetchAdmin = useCallback((options: PrefetchOptions = {}) => {
    const { delay = 300, staleTime = 60000 } = options;
    const key = 'admin-dashboard';

    if (prefetchedRef.current.has(key)) {
      return;
    }

    const existingTimer = timersRef.current.get(key);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(() => {
      // Prefetch common admin data
      queryClient.prefetchQuery({
        queryKey: ['admin', 'users'],
        staleTime,
      });
      queryClient.prefetchQuery({
        queryKey: standardQueryKeys.projects.lists(),
        staleTime,
      });

      prefetchedRef.current.add(key);
      timersRef.current.delete(key);
      logger.debug('PREFETCH', 'Admin dashboard prefetched');
    }, delay);

    timersRef.current.set(key, timer);
  }, [queryClient]);

  /**
   * Cancel a specific prefetch operation
   */
  const cancelPrefetch = useCallback((key: string) => {
    const timer = timersRef.current.get(key);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(key);
    }
  }, []);

  /**
   * Cancel all pending prefetch operations
   */
  const cancelAll = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  /**
   * Reset prefetch cache (useful when data changes)
   */
  const resetPrefetchCache = useCallback(() => {
    prefetchedRef.current.clear();
  }, []);

  /**
   * Smart route-based prefetch (predicts likely next navigation)
   */
  const prefetchRoute = useCallback((currentPath: string) => {
    // Route prediction patterns based on user behavior
    if (currentPath === '/') {
      // User on home → likely to view projects list
      prefetchProjects({ delay: 0 });
    } else if (currentPath.startsWith('/projects/') && !currentPath.includes('/diary')) {
      // User on project detail → likely to view diary/specs
      const projectId = currentPath.split('/')[2];
      if (projectId) {
        prefetchDiary(projectId, { delay: 1000 }); // Delayed prefetch
      }
    } else if (currentPath === '/projects') {
      // User on projects list → prefetch first few projects
      const cachedProjects = queryClient.getQueryData<any[]>(standardQueryKeys.projects.lists());
      if (cachedProjects && cachedProjects.length > 0) {
        cachedProjects.slice(0, 3).forEach(project => {
          prefetchProject(project.id, { delay: 2000, priority: 'low' });
        });
      }
    }
  }, [prefetchProjects, prefetchDiary, prefetchProject, queryClient]);

  return {
    prefetchProjects,
    prefetchProject,
    prefetchDiary,
    prefetchAdmin,
    prefetchRoute,
    cancelPrefetch,
    cancelAll,
    resetPrefetchCache,
  };
};
